import { ReactNode, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ButtonInput } from "../../../components/shared/Inputs/ButtonInput";
import { CheckboxInput } from "../../../components/shared/Inputs/CheckboxInput";
import { RadioGroupInput } from "../../../components/shared/Inputs/RadioGroupInput";
import SelectInput from "../../../components/shared/Inputs/SelectInput";
import { TextInput } from "../../../components/shared/Inputs/TextInput";
import LoadingWheel from "../../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../../contexts/PageTitleContext";
import {
  useReturnPolicies,
  useReturnTemplates,
  useReturnsSettings,
} from "../../../data/useReturnsWarehouse";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import {
  ReturnsSettings as IReturnsSettings,
  ReturnTemplate,
} from "../../../models/returns/IReturnsWarehouseApi";
import formatDateTime from "../../../utils/formatDateTime";
import { INBOX_DAY_TIME } from "../../../utils/inboxDates";

const EBAY_POLICIES_URL = "https://www.ebay.co.uk/bp/policies";
const DEFAULT_ENGINE_CODE_CATEGORIES = ["Engine", "Gearbox"];

type FormValues = {
  holdingBayLocationId: string;
  defaultOutcome: "holding" | "stock";
  nudgeRule: string;
  suggestedBinRule: "previous" | "nearest";
  relistOnLocate: boolean;
  engineCodeOnlyEnginesGearboxes: boolean;
  requiredPhotos: string;
  partProWindowDays: string;
  partProResponseDays: string;
  partProChangedMindPostage: "buyer" | "seller";
  returnAddress: string;
};

const toForm = (s: IReturnsSettings): FormValues => ({
  holdingBayLocationId: s.holdingBayLocationId ?? "",
  defaultOutcome: s.defaultOutcome,
  nudgeRule: `${s.locateNudgeCount}/${s.locateNudgeDays}`,
  suggestedBinRule: s.suggestedBinRule,
  relistOnLocate: s.relistOnLocate,
  engineCodeOnlyEnginesGearboxes: s.engineCodeCheckCategories.length > 0,
  requiredPhotos: String(s.requiredPhotos),
  partProWindowDays: String(s.partProWindowDays),
  partProResponseDays: String(s.partProResponseDays),
  partProChangedMindPostage: s.partProChangedMindPostage,
  returnAddress: s.returnAddress ?? "",
});

const fromForm = (
  f: FormValues,
  loaded: IReturnsSettings,
): IReturnsSettings => {
  const [count, days] = f.nudgeRule.split("/").map((n) => parseInt(n, 10) || 0);
  return {
    ...loaded,
    holdingBayLocationId: f.holdingBayLocationId || null,
    defaultOutcome: f.defaultOutcome,
    locateNudgeCount: count,
    locateNudgeDays: days,
    suggestedBinRule: f.suggestedBinRule,
    relistOnLocate: f.relistOnLocate,
    engineCodeCheckCategories: f.engineCodeOnlyEnginesGearboxes
      ? loaded.engineCodeCheckCategories.length
        ? loaded.engineCodeCheckCategories
        : DEFAULT_ENGINE_CODE_CATEGORIES
      : [],
    requiredPhotos: parseInt(f.requiredPhotos, 10) || 3,
    partProWindowDays: parseInt(f.partProWindowDays, 10) || 30,
    partProResponseDays: parseInt(f.partProResponseDays, 10) || 3,
    partProChangedMindPostage: f.partProChangedMindPostage,
    returnAddress: f.returnAddress.trim(),
  };
};

function Section({
  title,
  badge,
  right,
  children,
  span = false,
}: {
  title: ReactNode;
  badge?: string;
  right?: ReactNode;
  children: ReactNode;
  span?: boolean;
}) {
  return (
    <section
      className={`overflow-hidden rounded-xl border border-gray-200 bg-white ${span ? "lg:col-span-2" : ""}`}
    >
      <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          {title}
          {badge && (
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-gray-500">
              {badge}
            </span>
          )}
        </h2>
        {right}
      </div>
      {children}
    </section>
  );
}

const FixedRow = ({
  title,
  help,
  right,
  last = false,
}: {
  title: string;
  help: string;
  right: ReactNode;
  last?: boolean;
}) => (
  <li
    className={`flex items-center justify-between gap-2 py-2 text-xs ${last ? "" : "border-b border-gray-100"}`}
  >
    <span>
      <strong className="font-semibold text-gray-900">{title}</strong>{" "}
      <span className="text-gray-500">· {help}</span>
    </span>
    {right}
  </li>
);

function TemplatesCard() {
  const { t } = useTranslation("returns");
  const { templates, saveTemplates } = useReturnTemplates();
  const { saveHandlers } = useDefaultCRUDHandlers("Message templates");
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  const rows: ReturnTemplate[] = templates.data ?? [];

  const startEdit = (tpl: ReturnTemplate) => {
    setEditing(tpl.key);
    setDraft(tpl.body);
  };

  const save = () => {
    if (!editing) return;
    const next = rows.map((r) =>
      r.key === editing ? { ...r, body: draft } : r,
    );
    saveTemplates.mutate(next, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        setEditing(null);
      },
    });
  };

  return (
    <Section
      title={t("Message templates for eBay responses")}
      right={
        <span className="text-[11px] text-gray-500">
          {t("Merge fields:")}{" "}
          {
            "{buyer_first_name} {part_title} {refund_amount} {deadline} {shop_name} {carrier} {tracking}"
          }
        </span>
      }
    >
      {templates.isLoading && (
        <div className="p-4">
          <LoadingWheel />
        </div>
      )}
      {templates.isError && (
        <p className="p-4 text-xs text-red-700">
          {t("Couldn't load the templates.")}
        </p>
      )}
      <ul className="flex flex-col px-2.5 py-1.5 text-xs">
        {rows.map((tpl, i) => (
          <li
            key={tpl.key}
            className={
              i < rows.length - 1 || editing === tpl.key
                ? "border-b border-gray-100"
                : ""
            }
          >
            <div className="flex items-center justify-between gap-2 px-1.5 py-2">
              <strong className="font-semibold text-gray-900">
                {tpl.label}
              </strong>
              {editing === tpl.key ? (
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="text-xs font-semibold text-gray-500 hover:text-gray-700"
                >
                  {t("Cancel")}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => startEdit(tpl)}
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700"
                >
                  {t("Edit")}
                </button>
              )}
            </div>
            {editing === tpl.key && (
              <div className="flex flex-col gap-2 px-1.5 pb-3">
                <textarea
                  rows={6}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-xs text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] text-gray-500">
                    {t(
                      "Merge fields are filled in when the message is sent: {{fields}}",
                      {
                        fields:
                          "{buyer_first_name} {part_title} {refund_amount} {deadline} {shop_name} {rma}",
                      },
                    )}
                  </span>
                  <ButtonInput
                    label={t("Save")}
                    onClick={save}
                    classes={undefined}
                    isLoading={saveTemplates.isLoading}
                  />
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
}

/** Admin › Returns: eBay's policies read-only, plus the warehouse side Wilma adds. */
export default function ReturnsSettings() {
  const { t } = useTranslation("returns");
  const { setInfo } = usePageNameContext();
  const { settings, saveSettings } = useReturnsSettings();
  const policies = useReturnPolicies();
  const { saveHandlers } = useDefaultCRUDHandlers("Returns settings");

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      holdingBayLocationId: "",
      defaultOutcome: "holding",
      nudgeRule: "10/2",
      suggestedBinRule: "previous",
      relistOnLocate: false,
      engineCodeOnlyEnginesGearboxes: true,
      requiredPhotos: "3",
      partProWindowDays: "30",
      partProResponseDays: "3",
      partProChangedMindPostage: "buyer",
      returnAddress: "",
    },
  });

  useEffect(() => {
    setInfo({
      name: "Returns settings",
      desc: "eBay return policies, warehouse checks, holding bay and message templates",
    });
  }, [setInfo]);

  useEffect(() => {
    if (settings.data) reset(toForm(settings.data));
  }, [settings.data, reset]);

  const onSubmit = (data: FormValues) => {
    if (!settings.data) return;
    saveSettings.mutate(fromForm(data, settings.data), saveHandlers);
  };

  // The fixed choices plus whatever is stored now, so an older or hand-set value still shows (as nudgeRule does).
  const withCurrent = (ids: string[], current: string | undefined) =>
    !current || ids.includes(current) ? ids : [...ids, current];

  if (settings.isLoading) {
    return (
      <div className="p-2">
        <LoadingWheel />
      </div>
    );
  }

  const saveButton = (
    <ButtonInput
      label={t("Save")}
      isSubmit
      onClick={undefined}
      classes={undefined}
      disabled={!isDirty || !settings.data}
      isLoading={saveSettings.isLoading}
    />
  );

  return (
    <div className="space-y-4 p-2">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-medium text-gray-900">
            {t("Returns settings")}
          </h1>
          <p className="mt-1 text-xs text-gray-500">
            {t(
              "eBay owns the returns policy. Wilma reads it from your eBay account and adds a standard set of checks for returns that come back.",
            )}
          </p>
        </div>
        {policies.data?.lastSyncedAt && (
          <span className="text-[11px] text-gray-500">
            {t("Last synced from eBay: {{when}}", {
              when: formatDateTime(policies.data.lastSyncedAt, INBOX_DAY_TIME),
            })}
          </span>
        )}
      </div>
      {settings.isError && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
          {t(
            "Couldn't load the returns settings. Is the Returns feature switched on for this Wilma?",
          )}
        </p>
      )}

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2"
      >
        <Section
          span
          title={t("eBay return policies")}
          badge={t("Read-only · from eBay business policies")}
          right={
            <a
              href={EBAY_POLICIES_URL}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold text-primary-600 hover:text-primary-700"
            >
              {t("Edit on eBay")} ↗
            </a>
          }
        >
          {policies.isLoading && (
            <div className="p-4">
              <LoadingWheel />
            </div>
          )}
          {policies.isError && (
            <p className="p-4 text-xs text-red-700">
              {t("Couldn't load the policies from eBay.")}
            </p>
          )}
          {policies.data && (
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-2 font-semibold">
                    {t("Policy name")}
                  </th>
                  <th className="px-3 py-2 font-semibold">{t("Returns")}</th>
                  <th className="px-3 py-2 font-semibold">{t("Window")}</th>
                  <th className="px-3 py-2 font-semibold">
                    {t("Return postage")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {policies.data.policies.length === 0 && (
                  <tr className="border-t border-gray-100">
                    <td colSpan={4} className="px-4 py-3 text-gray-500">
                      {t("No return policies have come through from eBay yet.")}
                    </td>
                  </tr>
                )}
                {policies.data.policies.map((p) => (
                  <tr key={p.id} className="border-t border-gray-100">
                    <td className="px-4 py-2 font-semibold text-gray-900">
                      {p.name}
                    </td>
                    <td className="px-3 py-2">
                      {p.returnsAccepted ? (
                        t("Accepted")
                      ) : (
                        <span className="font-semibold text-red-700">
                          {t("Not accepted")}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {p.returnsAccepted && p.days
                        ? t("{{count}} days", { count: p.days })
                        : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {p.returnsAccepted ? (p.postagePayer ?? "—") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-gray-200 bg-gray-50 px-4 py-2.5 text-xs">
            <span className="text-gray-500">{t("eBay return address")}</span>
            <span className="font-medium text-gray-900">
              {watch("returnAddress") || policies.data?.returnAddress || "—"}
            </span>
            <span className="ml-auto text-gray-500">
              {t(
                "Buyers can always return not-as-described items whatever the policy; you pay return postage for those (eBay rule).",
              )}
            </span>
          </div>
          <div className="px-4 pb-3 pt-2">
            <TextInput
              label={t("Return address shown to buyers and on labels")}
              register={register}
              registerName="returnAddress"
              rounded
              cols=""
            />
          </div>
        </Section>

        <Section
          title={t("Standard return checks")}
          badge={t("Same for every part")}
          right={
            <span className="text-[11px] text-gray-500">
              {t("Asked in Received and checked on the return page")}
            </span>
          }
        >
          <ol className="flex flex-col px-4 py-2">
            <FixedRow
              title={t("Correct part?")}
              help={t("Tag no. scanned against the return")}
              right={
                <span className="text-[11px] text-gray-500">{t("Always")}</span>
              }
            />
            <FixedRow
              title={t("Part number matches?")}
              help={t("shown from the listing")}
              right={
                <span className="text-[11px] text-gray-500">{t("Always")}</span>
              }
            />
            <FixedRow
              title={t("Engine code matches?")}
              help={t("from the donor vehicle")}
              right={
                <CheckboxInput
                  label={t("Engines & gearboxes only")}
                  register={register}
                  registerName="engineCodeOnlyEnginesGearboxes"
                  cols=""
                  rounded
                />
              }
            />
            <FixedRow
              title={t("Same condition as dispatched?")}
              help={t('a "No" asks what\'s different')}
              right={
                <span className="text-[11px] text-gray-500">{t("Always")}</span>
              }
              last
            />
          </ol>
          <div className="border-t border-gray-200 bg-gray-50 px-4 py-2.5 text-[11px] text-gray-500">
            {t(
              "The answers and the condition band stay on the return permanently. Photos and putting the part away are done in Wilma Yard.",
            )}
          </div>
          <div className="flex items-center px-4 pb-3 pt-2">{saveButton}</div>
        </Section>

        <TemplatesCard />

        <Section
          title={
            <>
              <span className="rounded bg-gray-900 px-1.5 py-0.5 text-[10px] font-bold text-white">
                PartPro
              </span>
              {t("PartPro returns")}
            </>
          }
          right={
            <span className="text-[11px] text-gray-500">
              {t(
                "PartPro has no rules of its own yet — the one place Wilma sets something",
              )}
            </span>
          }
        >
          <div className="grid grid-cols-1 gap-3 px-4 py-3 text-xs sm:grid-cols-2">
            <SelectInput
              label={t("Return window")}
              register={register}
              registerName="partProWindowDays"
              options={withCurrent(
                ["14", "30", "60"],
                watch("partProWindowDays"),
              ).map((n) => ({
                id: n,
                name: t("{{count}} days from delivery", { count: Number(n) }),
              }))}
              setValue={setValue}
              watch={watch}
              cols=""
              rounded
            />
            <SelectInput
              label={t("Response time we hold ourselves to")}
              register={register}
              registerName="partProResponseDays"
              options={[
                { id: "1", name: t("1 business day") },
                { id: "3", name: t("3 business days (same as eBay)") },
                ...(["1", "3"].includes(watch("partProResponseDays"))
                  ? []
                  : [
                      {
                        id: watch("partProResponseDays"),
                        name: t("{{count}} business days", {
                          count: Number(watch("partProResponseDays")),
                        }),
                      },
                    ]),
              ]}
              setValue={setValue}
              watch={watch}
              cols=""
              rounded
            />
            <fieldset className="flex flex-col gap-1.5 text-gray-700">
              <legend className="mb-1 text-sm font-medium text-gray-500">
                {t("Return postage – changed mind")}
              </legend>
              <RadioGroupInput
                register={register}
                registerName="partProChangedMindPostage"
                setValue={setValue}
                watch={watch}
                options={[
                  { value: "buyer", name: t("Buyer pays") },
                  { value: "seller", name: t("We pay") },
                ]}
              />
            </fieldset>
            <div className="flex flex-col gap-1 text-gray-700">
              <span className="text-sm font-medium text-gray-500">
                {t("Return postage – not as described")}
              </span>
              <span className="font-semibold text-gray-900">
                {t("We pay")}{" "}
                <span className="font-normal text-gray-500">
                  · {t("mirrors eBay, not editable")}
                </span>
              </span>
            </div>
          </div>
          <div className="flex items-center px-4 pb-3">{saveButton}</div>
        </Section>
      </form>
    </div>
  );
}
